import { useParams, Link } from 'react-router-dom';
import { RiArrowLeftLine, RiMapPin2Line, RiTimeLine, RiWalletLine, RiCalendarLine, RiArrowRightLine, RiBusLine } from '@remixicon/react';
import { Footer } from '../components/layout/Footer';
import { usePageMeta } from '../hooks/usePageMeta';
import { TRAJET_MAP } from '../data/trajetRoutes';

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 bg-white rounded-2xl border border-line-light p-4">
      <div className="w-9 h-9 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <p className="font-display text-[11px] text-ink-muted uppercase tracking-wide font-semibold">{label}</p>
        <p className="font-display text-[15px] font-bold text-ink mt-0.5">{value}</p>
      </div>
    </div>
  );
}

export function TrajetPage() {
  const { slug } = useParams<{ slug: string }>();
  const route = slug ? TRAJET_MAP[slug] : undefined;

  if (!route) {
    usePageMeta('Page introuvable · idaNroh', 'Cette page n\'existe pas.');
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-display text-lg text-ink">Trajet introuvable.</p>
        <Link to="/" className="text-brand underline font-display text-sm">Retour à l'accueil</Link>
      </div>
    );
  }

  const fromLower = route.from.toLowerCase();
  const toLower = route.to.toLowerCase().replace(/\s/g, '-').replace(/é/g, 'e').replace(/è/g, 'e').replace(/ê/g, 'e').replace(/î/g, 'i').replace(/â/g, 'a');

  const title = `Bus ${route.from} → ${route.to} : Horaires, prix et places en direct`;
  const description = `Vérifie l'affluence des bus SOGRAL ${route.from}–${route.to} avant de partir. ${route.departsPerDay}, ${route.priceRange}, durée ${route.duration}. Évite la queue à la gare de Kharouba.`;
  const keywords = `bus ${fromLower} ${toLower}, sogral ${toLower}, horaire bus ${toLower}, affluence bus algérie, prix bus ${fromLower} ${toLower}, gare routière ${route.to}, إذا نروح`;
  const canonical = `https://idanroh-dz.com/trajet/${route.slug}/`;

  usePageMeta(title, description, keywords);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: route.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'idaNroh', item: 'https://idanroh-dz.com/' },
      { '@type': 'ListItem', position: 2, name: `Bus ${route.from} → ${route.to}`, item: canonical },
    ],
  };

  const travelSchema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    name: `Bus ${route.from} ↔ ${route.to}`,
    fromLocation: {
      '@type': 'Place',
      name: route.from,
      address: { '@type': 'PostalAddress', addressCountry: 'DZ', addressRegion: route.from },
    },
    toLocation: {
      '@type': 'Place',
      name: route.to,
      address: { '@type': 'PostalAddress', addressCountry: 'DZ', addressRegion: route.to },
    },
    agent: { '@type': 'Organization', name: 'idaNroh', url: 'https://idanroh-dz.com/' },
  };

  const relatedRoutes = route.related
    .map((s) => TRAJET_MAP[s])
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-surface-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(travelSchema) }} />

      {/* Header */}
      <div className="bg-brand px-5 lg:px-10 py-4 flex items-center gap-4">
        <Link
          to="/"
          className="flex items-center gap-1.5 text-white/80 hover:text-white font-display text-[14px] transition-colors"
        >
          <RiArrowLeftLine className="h-4 w-4" />
          Accueil
        </Link>
        <img src="/new_assets/LogoH.svg" alt="idaNroh" className="h-8 w-auto" draggable={false} />
      </div>

      {/* Hero */}
      <div className="bg-brand pb-8 pt-6 px-5 lg:px-10">
        <div className="max-w-[760px] mx-auto">
          <nav className="flex items-center gap-1.5 text-white/60 font-display text-[12px] mb-4">
            <Link to="/" className="hover:text-white transition-colors">idaNroh</Link>
            <span>/</span>
            <span className="text-white/80">Bus {route.from} → {route.to}</span>
          </nav>
          <h1 className="font-display font-bold text-white text-[24px] lg:text-[30px] leading-tight mb-3">
            Bus {route.from} → {route.to}
            <span className="block text-white/70 text-[16px] lg:text-[18px] font-medium mt-1">
              Horaires, prix et places disponibles en direct
            </span>
          </h1>
          <p className="font-display text-white/80 text-[14px] leading-relaxed max-w-xl">
            {route.intro}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[760px] mx-auto px-5 lg:px-8 py-8 space-y-8">

        {/* Stats */}
        <section>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={<RiMapPin2Line className="h-4 w-4 text-brand" />} label="Distance" value={route.distance} />
            <StatCard icon={<RiTimeLine className="h-4 w-4 text-brand" />} label="Durée" value={route.duration} />
            <StatCard icon={<RiWalletLine className="h-4 w-4 text-brand" />} label="Prix" value={route.priceRange} />
            <StatCard icon={<RiCalendarLine className="h-4 w-4 text-brand" />} label="Fréquence" value={route.departsPerDay} />
          </div>
        </section>

        {/* CTA */}
        <section className="bg-brand/5 border border-brand/20 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-display font-bold text-ink text-[16px] mb-1">Voir les départs d'aujourd'hui</p>
            <p className="font-display text-sm text-ink-muted">
              Places disponibles et affluence en temps réel depuis la gare de Kharouba.
            </p>
          </div>
          <Link
            to="/"
            className="shrink-0 flex items-center gap-2 bg-brand text-white font-display font-semibold text-[14px] px-5 py-3 rounded-xl hover:opacity-90 transition-opacity"
          >
            <RiBusLine className="h-4 w-4" />
            Voir les départs
            <RiArrowRightLine className="h-4 w-4" />
          </Link>
        </section>

        {/* Horaires */}
        <section className="bg-white rounded-2xl border border-line-light p-6">
          <h2 className="font-display font-bold text-ink text-[18px] mb-4">Horaires et fréquence</h2>
          <p className="font-display text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">{route.frequency}.</strong>{' '}
            Les horaires exacts varient selon les transporteurs et la saison. idaNroh te montre les départs SOGRAL
            disponibles aujourd'hui en temps réel — inutile de mémoriser un horaire fixe.
          </p>
          <div className="mt-4 p-4 bg-warn/8 border border-warn/20 rounded-xl">
            <p className="font-display text-sm text-ink leading-relaxed">
              <strong>Conseil :</strong> le vendredi matin et le dimanche soir sont les créneaux les plus
              chargés. Consulte l'onglet <strong>Prévision</strong> sur idaNroh pour connaître le créneau
              le moins chargé de la semaine.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="font-display font-bold text-ink text-[18px] mb-4">Questions fréquentes</h2>
          <div className="space-y-3">
            {route.faqs.map((faq, i) => (
              <details
                key={i}
                className="bg-white rounded-2xl border border-line-light overflow-hidden group"
              >
                <summary className="px-5 py-4 font-display font-semibold text-[14px] text-ink cursor-pointer flex items-center justify-between gap-3 list-none">
                  {faq.q}
                  <RiArrowRightLine className="h-4 w-4 text-ink-muted shrink-0 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-4 font-display text-sm text-ink-muted leading-relaxed border-t border-line-light pt-3">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Routes similaires */}
        {relatedRoutes.length > 0 && (
          <section>
            <h2 className="font-display font-bold text-ink text-[18px] mb-4">Autres liaisons depuis Alger</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {relatedRoutes.map((r) => (
                <Link
                  key={r.slug}
                  to={`/trajet/${r.slug}/`}
                  className="bg-white rounded-2xl border border-line-light p-4 hover:border-brand/40 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <RiBusLine className="h-4 w-4 text-brand" />
                    <span className="font-display font-semibold text-[13px] text-ink">{r.from} → {r.to}</span>
                  </div>
                  <p className="font-display text-[12px] text-ink-muted">{r.duration} · {r.priceRange}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="font-display text-[11px] text-ink-faint text-center leading-relaxed pb-4">
          Les horaires et prix affichés sont indicatifs et basés sur les données SOGRAL. Consulte idaNroh
          pour les informations en temps réel avant ton départ.
        </p>
      </div>

      <Footer />
    </div>
  );
}
